/**
 * Display formatting.
 *
 * The engine is SI throughout (non-negotiable #3); imperial exists only here, behind a setting.
 * Every number is rendered with its unit at reduced emphasis — never a bare numeral (UI.md).
 */

export interface UnitPrefs {
  imperial: boolean;
}

const M_TO_FT = 3.280839895;
const M3S_TO_CFS = 35.3146667;
const M3_TO_ACREFT = 1 / 1233.48184;

export function elevation(m: number, p: UnitPrefs): { value: string; unit: string } {
  return p.imperial
    ? { value: (m * M_TO_FT).toFixed(1), unit: 'ft' }
    : { value: m.toFixed(2), unit: 'm' };
}

export function distance(m: number, p: UnitPrefs): { value: string; unit: string } {
  return p.imperial
    ? { value: (m * M_TO_FT).toFixed(1), unit: 'ft' }
    : { value: m.toFixed(2), unit: 'm' };
}

export function flow(q: number, p: UnitPrefs): { value: string; unit: string } {
  return p.imperial
    ? { value: Math.round(q * M3S_TO_CFS).toLocaleString('en-US'), unit: 'cfs' }
    : { value: Math.round(q).toLocaleString('en-US'), unit: 'm³/s' };
}

export function volume(v: number, p: UnitPrefs): { value: string; unit: string } {
  return p.imperial
    ? { value: (v * M3_TO_ACREFT).toFixed(0), unit: 'ac·ft' }
    : { value: (v / 1e6).toFixed(1), unit: '×10⁶ m³' };
}

export function power(mw: number): { value: string; unit: string } {
  return { value: Math.round(mw).toLocaleString('en-US'), unit: 'MW' };
}

export function energy(mwh: number): { value: string; unit: string } {
  return mwh >= 1000
    ? { value: (mwh / 1000).toFixed(1), unit: 'GWh' }
    : { value: mwh.toFixed(0), unit: 'MWh' };
}

export function money(v: number): string {
  const abs = Math.abs(v);
  const sign = v < 0 ? '−' : '';
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(0)}k`;
  return `${sign}$${abs.toFixed(0)}`;
}

export function moneyExact(v: number): string {
  const sign = v < 0 ? '−' : '';
  return `${sign}$${Math.abs(Math.round(v)).toLocaleString('en-US')}`;
}

export function price(v: number): { value: string; unit: string } {
  return { value: `${v < 0 ? '−' : ''}$${Math.abs(v).toFixed(0)}`, unit: '/MWh' };
}

export function temperature(c: number, p: UnitPrefs): { value: string; unit: string } {
  return p.imperial
    ? { value: (c * 1.8 + 32).toFixed(0), unit: '°F' }
    : { value: c.toFixed(1), unit: '°C' };
}

export function percent(v: number, digits = 1): { value: string; unit: string } {
  return { value: v.toFixed(digits), unit: '%' };
}

/** Sim minutes as a compact duration, e.g. "2 d 06:15". */
export function duration(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  const mm = m % 60;
  const hhmm = `${String(h).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
  return d > 0 ? `${d} d ${hhmm}` : hhmm;
}
